import { useState, useEffect } from "react";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { ExternalLink, Briefcase, GraduationCap, RefreshCw, Car, BarChart3, Link2, Plus, Save, Edit3, Trash2 } from "lucide-react";

const ICONS = [Briefcase, GraduationCap, RefreshCw, Car, BarChart3, Link2];

const parseLink = (item) => {
  try {
    const data = JSON.parse(item.content);
    return { ...item, url: data.url || "", description: data.description || "" };
  } catch {
    return { ...item, url: item.content, description: "" };
  }
};

export default function LinkUtili() {
  const { isAdmin } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newLink, setNewLink] = useState({ title: "", description: "", url: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", url: "" });

  useEffect(() => { fetchLinks(); }, []);

  const fetchLinks = async () => {
    try {
      const res = await axios.get(`${API}/content/link-utili`);
      setLinks(res.data.map(parseLink));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const addLink = async () => {
    if (!newLink.title.trim() || !newLink.url.trim()) {
      toast.error("Inserisci titolo e link");
      return;
    }
    try {
      await axios.post(`${API}/content`, {
        section: "link-utili",
        content_type: "link",
        title: newLink.title.trim(),
        content: JSON.stringify({ url: newLink.url.trim(), description: newLink.description.trim() }),
        order: links.length
      });
      toast.success("Link aggiunto");
      setNewLink({ title: "", description: "", url: "" });
      setShowAdd(false);
      fetchLinks();
    } catch {
      toast.error("Errore");
    }
  };

  const startEdit = (link, e) => {
    e.preventDefault(); e.stopPropagation();
    setEditingId(link.id);
    setEditForm({ title: link.title, description: link.description, url: link.url });
  };

  const saveEdit = async (id) => {
    if (!editForm.title.trim() || !editForm.url.trim()) {
      toast.error("Inserisci titolo e link");
      return;
    }
    try {
      await axios.put(`${API}/content/${id}`, {
        title: editForm.title.trim(),
        content: JSON.stringify({ url: editForm.url.trim(), description: editForm.description.trim() })
      });
      toast.success("Aggiornato");
      setEditingId(null);
      fetchLinks();
    } catch {
      toast.error("Errore");
    }
  };

  const deleteLink = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await axios.delete(`${API}/content/${id}`);
      toast.success("Eliminato");
      fetchLinks();
    } catch {
      toast.error("Errore");
    }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center" data-testid="link-utili-loading">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="link-utili-page">
      <PageHero image={HERO_IMAGES.home} kicker="Risorse" title="Link Utili" subtitle="Accesso rapido alle piattaforme e strumenti della community" />
      <SectionBg type="charts" />
      <div className="container py-8 relative">

        {/* Admin Add Link */}
        {isAdmin && (
          <div className="mb-10 max-w-lg mx-auto">
            {!showAdd ? (
              <button
                onClick={() => setShowAdd(true)}
                className="btn-primary flex items-center gap-2 mx-auto text-sm"
                data-testid="add-link-btn"
              >
                <Plus size={16} /> Aggiungi Link
              </button>
            ) : (
              <div className="glass-card p-5 space-y-3" data-testid="add-link-form">
                <h3 className="text-lg font-semibold text-center" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>
                  Nuovo Link
                </h3>
                <input type="text" value={newLink.title} onChange={(e) => setNewLink({ ...newLink, title: e.target.value })} className="admin-input" placeholder="Titolo" data-testid="new-link-title-input" />
                <input type="text" value={newLink.description} onChange={(e) => setNewLink({ ...newLink, description: e.target.value })} className="admin-input" placeholder="Descrizione (opzionale)" data-testid="new-link-description-input" />
                <input type="text" value={newLink.url} onChange={(e) => setNewLink({ ...newLink, url: e.target.value })} className="admin-input" placeholder="Link (https://...)" data-testid="new-link-url-input" />
                <div className="flex gap-3">
                  <button onClick={addLink} className="btn-primary flex-1 py-2 text-sm flex items-center justify-center gap-2" data-testid="save-new-link-btn">
                    <Save size={14} /> Salva
                  </button>
                  <button onClick={() => { setShowAdd(false); setNewLink({ title: "", description: "", url: "" }); }} className="btn-secondary flex-1 py-2 text-sm">
                    Annulla
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Links Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
          {links.map((link, index) => {
            const Icon = ICONS[index % ICONS.length];

            if (editingId === link.id) {
              return (
                <div key={link.id} className="glass-card p-5 space-y-3" data-testid={`edit-link-form-${index}`}>
                  <input type="text" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="admin-input text-sm" placeholder="Titolo" data-testid="edit-link-title-input" />
                  <input type="text" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="admin-input text-sm" placeholder="Descrizione" data-testid="edit-link-description-input" />
                  <input type="text" value={editForm.url} onChange={(e) => setEditForm({ ...editForm, url: e.target.value })} className="admin-input text-sm" placeholder="Link (https://...)" data-testid="edit-link-url-input" />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(link.id)} className="btn-primary flex-1 py-1.5 text-sm" data-testid="save-edit-link-btn">Salva</button>
                    <button onClick={() => setEditingId(null)} className="btn-secondary flex-1 py-1.5 text-sm">Annulla</button>
                  </div>
                </div>
              );
            }

            return (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`glass-card p-5 sm:p-6 group cursor-pointer animate-fadeIn stagger-${Math.min(index + 1, 4)} transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] relative`}
                style={{ 
                  borderColor: "rgba(47, 216, 168, 0.2)",
                  textDecoration: "none"
                }}
                data-testid={`link-card-${index}`}
              >
                {/* Admin Edit/Delete */}
                {isAdmin && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <button onClick={(e) => startEdit(link, e)} className="p-1.5 rounded" style={{ backgroundColor: "rgba(47,216,168,0.2)" }} data-testid={`edit-link-btn-${index}`}>
                      <Edit3 size={13} style={{ color: "var(--mm-gold)" }} />
                    </button>
                    <button onClick={(e) => deleteLink(link.id, e)} className="p-1.5 rounded" style={{ backgroundColor: "rgba(239,68,68,0.2)" }} data-testid={`delete-link-btn-${index}`}>
                      <Trash2 size={13} style={{ color: "#EF4444" }} />
                    </button>
                  </div>
                )}

                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110"
                    style={{ backgroundColor: "rgba(47, 216, 168, 0.15)" }}
                  >
                    <Icon size={26} style={{ color: "var(--mm-gold)" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 
                        className="text-lg sm:text-xl font-bold gold-text truncate"
                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                      >
                        {link.title}
                      </h3>
                      <ExternalLink 
                        size={18} 
                        style={{ color: "var(--mm-gold)" }}
                        className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 flex-shrink-0"
                      />
                    </div>
                    {link.description && (
                      <p 
                        className="text-sm sm:text-base"
                        style={{ color: "var(--mm-text-secondary)" }}
                      >
                        {link.description}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Hover glow effect */}
                <div 
                  className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{ 
                    boxShadow: "inset 0 0 30px rgba(47, 216, 168, 0.1)",
                    borderRadius: "12px"
                  }}
                />
              </a>
            );
          })}
        </div>

        {links.length === 0 && !isAdmin && (
          <p className="text-center text-sm" style={{ color: "var(--mm-text-muted)" }}>
            I link verranno aggiunti a breve
          </p>
        )}

        {links.length > 0 && (
          <p 
            className="text-center mt-8 sm:mt-12 text-sm sm:text-base animate-fadeIn"
            style={{ color: "var(--mm-text-muted)" }}
          >
            Tocca una card per aprire il link
          </p>
        )}
      </div>
    </div>
  );
}
